import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { Pagination } from '../components/ui/Pagination';
import { Sheet } from '../components/ui/Sheet';
import { EstablishmentForm } from '../components/EstablishmentForm';
import { apiFetch, parseJsonResponse, isNetworkError, isNetworkErrorResponse } from '../utils/api';

interface Establishment {
  id: number;
  client_id: number;
  name_establishment: string;
  code_establishment_hioffice: number;
  estado_id: number;
  telephone_establishment: string | null;
  logo_path: string | null;
  font_title_id: number | null;
  font_subtitle_id: number | null;
  font_text_id: number | null;
  font_script_id: number | null;
  font_size_title: number;
  font_size_subtitle: number;
  font_size_text: number;
  export_id_articulos: number | null;
  export_id_comments: number | null;
  is_active: number;
  language: string;
  created_at: string;
  updated_at: string;
  client_name?: string;
  estado_name?: string;
}

export const Establishments: React.FC = () => {
  const { token } = useAuth();
  const { can } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingEstablishment, setEditingEstablishment] = useState<Establishment | null>(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Leer estado del formulario desde URL params
  const showForm = searchParams.get('form') === 'open';
  const editId = searchParams.get('edit');

  const fetchEstablishments = async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });
      if (search) {
        params.append('search', search);
      }

      const route = `establishments/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      // Si es error de red, mantener datos actuales y mostrar mensaje
      if (isNetworkErrorResponse(res)) {
        setError('Error de conexión. Verifica tu conexión a internet.');
        return;
      }

      const json = await parseJsonResponse(res);

      // Si es error 401, NO cerrar sesión aquí - solo mostrar el error
      // Solo checkAuth puede cerrar la sesión, no las peticiones individuales
      if (res.status === 401) {
        console.warn('Error 401 en fetchEstablishments. NO cerrando sesión aquí.', { status: res.status, json });
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'No autorizado';
        setError(errorMessage);
        setLoading(false);
        return; // Salir sin lanzar error para evitar que se propague
      }

      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar establecimientos';
        throw new Error(errorMessage);
      }

      setEstablishments(json.data.establishments || []);
      setPagination(json.data.pagination || pagination);
    } catch (err: any) {
      if (isNetworkError(err)) {
        setError('Error de conexión. Verifica tu conexión a internet.');
      } else {
        // Si es un error 401, podría ser un problema de autenticación
        // No cerrar sesión aquí, solo mostrar el error
        // La sesión se cerrará solo si checkAuth detecta un 401 real
      setError(err.message || 'Error inesperado');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchEstablishments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pagination.page, search]);

  // Cargar establecimiento para editar si hay editId en URL
  useEffect(() => {
    if (editId && establishments.length > 0 && !editingEstablishment) {
      const establishment = establishments.find(e => e.id === parseInt(editId));
      if (establishment) {
        setEditingEstablishment(establishment);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, establishments]);

  const handleCreate = () => {
    setEditingEstablishment(null);
    setSearchParams({ form: 'open' });
  };

  const handleEdit = (establishment: Establishment) => {
    setEditingEstablishment(establishment);
    setSearchParams({ form: 'open', edit: establishment.id.toString() });
  };

  const handleDelete = async (id: number) => {
    if (!token) return;
    
    if (!window.confirm('¿Estás seguro de que deseas eliminar este establecimiento?')) {
      return;
    }

    try {
      const res = await apiFetch(`establishments/delete&id=${id}`, { method: 'GET' }, token);
      
      // Si es error de red, mostrar mensaje pero no hacer nada más
      if (isNetworkErrorResponse(res)) {
        alert('Error de conexión. No se pudo eliminar el establecimiento.');
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        throw new Error(json.error || json.message || json.data?.error || 'Error al eliminar establecimiento');
      }

      fetchEstablishments();
    } catch (err: any) {
      if (isNetworkError(err)) {
        alert('Error de conexión. Verifica tu conexión a internet.');
      } else {
      alert(err.message || 'Error al eliminar establecimiento');
      }
    }
  };

  const handleFormSuccess = () => {
    setSearchParams({});
    setEditingEstablishment(null);
    fetchEstablishments();
  };

  const handleFormCancel = () => {
    setSearchParams({});
    setEditingEstablishment(null);
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchEstablishments();
  };

  // Calcular estadísticas rápidas
  const activeEstablishments = establishments.filter(e => e.is_active === 1).length;
  const inactiveEstablishments = establishments.length - activeEstablishments;

  // Función helper para formatear fechas
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  return (
    <DashboardLayout
      title="Gestión de Establecimientos"
      subtitle="Administra los establecimientos del sistema"
      showBackButton
      backPath="/management"
    >
      {/* Sheet para el formulario */}
      <Sheet
        isOpen={showForm}
        onClose={handleFormCancel}
        title={editingEstablishment ? 'Editar Establecimiento' : 'Nuevo Establecimiento'}
        subtitle={editingEstablishment ? 'Modifica la información del establecimiento' : 'Completa los datos para crear un nuevo establecimiento'}
        size="xl"
      >
        <EstablishmentForm
          establishment={editingEstablishment}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      </Sheet>

      <div className="space-y-6">
        {/* Estadísticas Rápidas */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-600">Total de Establecimientos</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{pagination.total || establishments.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-600">Establecimientos Activos</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{activeEstablishments}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-600">Establecimientos Inactivos</p>
                  <p className="text-xl sm:text-2xl font-bold text-red-600">{inactiveEstablishments}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

          {/* Actions Bar */}
            <Card>
          <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-between">
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    onSubmit={handleSearch}
                    placeholder="Buscar por nombre o código..."
                    className="flex-1 w-full sm:w-auto min-w-0"
                  />
                  {can('establishments.create') && (
                    <Button
                      variant="primary"
                      size="md"
                      onClick={handleCreate}
                      className="whitespace-nowrap w-full sm:w-auto"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span className="hidden sm:inline">Nuevo Establecimiento</span>
                      <span className="sm:hidden">Nuevo</span>
                    </Button>
                  )}
                </div>
          </CardContent>
            </Card>

          {/* Error Message */}
          {error && (
              <Alert variant="error">{error}</Alert>
          )}

          {/* Establishments Table */}
          {loading ? (
            <Card>
            <CardContent>
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
                <p className="text-gray-600">Cargando establecimientos...</p>
              </div>
            </CardContent>
            </Card>
          ) : establishments.length === 0 ? (
            <Card>
            <CardContent>
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay establecimientos registrados</h3>
                <p className="text-gray-600 mb-6">
                  {search ? 'No se encontraron establecimientos con los criterios de búsqueda.' : 'Comienza creando tu primer establecimiento.'}
                </p>
                {can('establishments.create') && (
                  <Button variant="primary" onClick={handleCreate}>
                    Crear Primer Establecimiento
                  </Button>
                )}
              </div>
            </CardContent>
            </Card>
          ) : (
            <>
              {/* Vista de Cards para Mobile/Tablet */}
              <div className="block md:hidden space-y-4">
                {establishments.map((establishment) => (
                  <Card key={establishment.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-1 truncate">
                            {establishment.name_establishment}
                          </h4>
                          <p className="text-xs text-gray-500 truncate">
                            {establishment.client_name || `Cliente ID: ${establishment.client_id}`}
                          </p>
                        </div>
                        <Badge variant={establishment.is_active ? 'success' : 'error'} className="ml-2 flex-shrink-0">
                          {establishment.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Código:</span>
                          <span className="text-gray-900 font-mono text-xs">{establishment.code_establishment_hioffice}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Estado:</span>
                          <span className="text-gray-900">{establishment.estado_name || `ID: ${establishment.estado_id}`}</span>
                        </div>
                        {establishment.telephone_establishment && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Teléfono:</span>
                            <span className="text-gray-900">{establishment.telephone_establishment}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Fechas */}
                      <div className="space-y-1 text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">
                        <div className="flex justify-between">
                          <span>Creado:</span>
                          <span>{formatDate(establishment.created_at)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Actualizado:</span>
                          <span>{formatDate(establishment.updated_at)}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
                        {can('establishments.edit') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(establishment)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Editar
                          </Button>
                        )}
                        {can('establishments.delete') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(establishment.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Eliminar
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {/* Paginación para mobile/tablet */}
                <div className="pt-4">
                  <Pagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    limit={pagination.limit}
                    onPageChange={(page) => setPagination({ ...pagination, page })}
                  />
                </div>
              </div>

              {/* Vista de Tabla para Desktop */}
              <Card className="hidden md:block">
                <div className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableHeaderCell>Nombre</TableHeaderCell>
                        <TableHeaderCell>Cliente</TableHeaderCell>
                        <TableHeaderCell className="hidden lg:table-cell">Código Hioffice</TableHeaderCell>
                        <TableHeaderCell className="hidden lg:table-cell">Estado</TableHeaderCell>
                        <TableHeaderCell className="hidden lg:table-cell">Teléfono</TableHeaderCell>
                        <TableHeaderCell>Estado Activo</TableHeaderCell>
                        <TableHeaderCell className="hidden lg:table-cell">Creado</TableHeaderCell>
                        <TableHeaderCell className="hidden lg:table-cell">Actualizado</TableHeaderCell>
                        <TableHeaderCell className="text-right">Acciones</TableHeaderCell>
                      </TableHeader>
                      <TableBody>
                        {establishments.map((establishment) => (
                          <TableRow key={establishment.id}>
                            <TableCell>
                              <div className="font-semibold text-gray-900">{establishment.name_establishment}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-gray-600">{establishment.client_name || `ID: ${establishment.client_id}`}</div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <div className="text-gray-600 font-mono text-xs">{establishment.code_establishment_hioffice}</div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <div className="text-gray-600">{establishment.estado_name || `ID: ${establishment.estado_id}`}</div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <div className="text-gray-600">{establishment.telephone_establishment || '-'}</div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={establishment.is_active ? 'success' : 'error'}>
                                {establishment.is_active ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <div className="text-xs text-gray-600">
                                {formatDate(establishment.created_at)}
                              </div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <div className="text-xs text-gray-600">
                                {formatDate(establishment.updated_at)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1 sm:gap-2 flex-wrap">
                                {can('establishments.edit') && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEdit(establishment)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-1.5 sm:p-2"
                                  >
                                    <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    <span className="hidden sm:inline">Editar</span>
                                  </Button>
                                )}
                                {can('establishments.delete') && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(establishment.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1.5 sm:p-2"
                                  >
                                    <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    <span className="hidden sm:inline">Eliminar</span>
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="border-t border-gray-200 p-4">
                    <Pagination
                      page={pagination.page}
                      pages={pagination.pages}
                      total={pagination.total}
                      limit={pagination.limit}
                      onPageChange={(page) => setPagination({ ...pagination, page })}
                    />
                  </div>
                </div>
              </Card>
            </>
          )}
      </div>
    </DashboardLayout>
  );
};

